import type { ValueOf } from '@/root/types/utilities'

interface CookieOptions {
  path?: string
  maxAge?: number
  httpOnly?: boolean
  secure?: boolean
  sameSite?: string
  domain?: string
}

/** 30 days */
const MAX_AGE = 30 * 24 * 60 * 60

const DEFAULT_COOKIE_OPTIONS: CookieOptions = {
  path: '/',
  maxAge: MAX_AGE,
  httpOnly: false,
  secure: true,
  sameSite: 'Lax',
}

const buildCookieString = (
  key: string,
  value: string,
  options: CookieOptions = {},
): string => {
  const config = { ...DEFAULT_COOKIE_OPTIONS, ...options }
  const parts = [`${key}=${value}`]

  if (config.path) parts.push(`Path=${config.path}`)
  if (config.maxAge) parts.push(`Max-Age=${config.maxAge}`)
  if (config.httpOnly) parts.push('HttpOnly')
  if (config.secure) parts.push('Secure')
  if (config.sameSite) parts.push(`SameSite=${config.sameSite}`)
  if (config.domain) parts.push(`Domain=${config.domain}`)

  return parts.join('; ')
}

export const COOKIE_KEYS = {
  TRACE_ID: 'trace_id',
  USER_ID: 'user_id',
  USER_SESSION_ID: 'user_session_id',
  REFRESH_TOKEN: 'refresh_token',
  TOKEN: 'token',
  DEVICE_ID: 'device_id',
} as const

const cookies = {
  getTokenCookie: (token: string, maxAge: number = MAX_AGE) => {
    return buildCookieString(COOKIE_KEYS.TOKEN, token, { maxAge })
  },
  getRefreshTokenCookie: (refreshToken: string, maxAge: number = MAX_AGE) => {
    return buildCookieString(COOKIE_KEYS.REFRESH_TOKEN, refreshToken, {
      maxAge,
      httpOnly: true,
    })
  },

  getClearTokensCookie: () => {
    return [
      buildCookieString(COOKIE_KEYS.REFRESH_TOKEN, '', { maxAge: 0 }),
      buildCookieString(COOKIE_KEYS.TOKEN, '', { maxAge: 0 }),
      buildCookieString(COOKIE_KEYS.TRACE_ID, '', { maxAge: 0 }),
      buildCookieString(COOKIE_KEYS.USER_ID, '', { maxAge: 0 }),
      buildCookieString(COOKIE_KEYS.USER_SESSION_ID, '', { maxAge: 0 }),
    ]
  },

  getGenericSessionCookie: (
    key: ValueOf<typeof COOKIE_KEYS>,
    value: string,
    maxAge: number = MAX_AGE,
  ) => {
    return buildCookieString(key, value, { maxAge })
  },
}

export default cookies
